import type { DefaultTheme } from 'styled-components/macro';

type BreakpointsKeysT = { [key in keyof typeof breakpointsNumInPx]: string };

/** Mobile breakpoints: tablet 768px, large mobile 425px */
export const breakpointsNumInPx = {
  /** Large mobile (e.g. iPhone 14 Pro Max 430px) */
  largeMobile: 425,
  /** Tablet */
  tablet: 768,
  /** Legacy – kept for compatibility */
  xs: 320,
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
  '2xl': 1440,
};
const breakpointsInEm: BreakpointsKeysT = {
  largeMobile: '26.5625em', // 425px
  tablet: '48em', // 768px
  xs: '20em',
  sm: '30em',
  md: '48em',
  lg: '62em',
  xl: '80em',
  '2xl': '90em',
};
export const QUERY_TYPE = '@media screen';
export const ORIENTATION = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
};
export const devices = {
  smallMobile: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.xs})`,
  mobile: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.sm})`,
  tablet: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.md})`,
  tabletLandscape: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.md}) and (orientation: ${ORIENTATION.LANDSCAPE})`,
  smallDesktop: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.lg})`,
  desktop: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.xl})`,
  largeDesktop: `${QUERY_TYPE} and (min-width: ${breakpointsInEm['2xl']})`,
  iOS: `${QUERY_TYPE} and (-webkit-min-device-pixel-ratio: 2)`,
};

/** Max-width queries for mobile breakpoints */
export const devicesMax = {
  /** Below 425px – small mobile */
  belowLargeMobile: `${QUERY_TYPE} and (max-width: 424px)`,
  /** Below 768px – tablet / large mobile */
  belowTablet: `${QUERY_TYPE} and (max-width: 767px)`,
};

const sharedTheme = {
  fontFamily: {
    primary: '"Roboto", sans-serif',
    secondary: '"Barlow", sans-serif',
  },
  fontSize: {
    xxs: '0.688rem',
    xs: '0.75rem',
    sm: '0.813rem',
    md: '0.875rem',
    lg: '0.938rem',
    xl: '1rem',
    '2xl': '1.125rem',
    '3xl': '1.313rem',
    '4xl': '1.5rem',
    '5xl': '1.875rem',
  },
  fontWeight: {
    hairline: 100,
    ultraLight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
  shadows: {
    variant1: '0px 4px 9px rgba(0, 0, 0, 0.08)',
    variant2: '0px 0px 6px 4px rgb(0 0 0 / 0.1)',
  },
  radius: {
    xs: '0.125rem',
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.625rem',
    '2xl': '4rem',
    half: '50%',
  },
  borders: {
    none: 'none',
    xs: '1px solid',
    sm: '2px solid',
    md: '3px solid',
    lg: '4px solid',
    xl: '5px solid',
    '2xl': '6px solid',
    '3xl': '8px solid',
    '4xl': '10px solid',
    '5xl': '12px solid',
  },
  filters: {
    whiteToViolet:
      'invert(34%) sepia(95%) saturate(2134%) hue-rotate(214deg) brightness(97%) contrast(89%)',
  },
  breakpointsNumInPx,
  devices,
  devicesMax,
};

const lightColors = {
  mode: 'light' as const,
  colors: {
    white: '#FFFFFF',
    black: '#000000',
    transparent: 'transparent',
    /** Card/modal/list surface - matches WarrQueue background.paper */
    surface: '#FFFFFF',
    /** Page background - matches WarrQueue background.default */
    backgroundDefault: '#F7F7F7',
    /** Lower section background - matches WarrQueue work item view background.backdrop */
    backdrop: '#F5F5F5',
    /** Focus ring - matches WarrQueue card focus highlight (primary.highlight) */
    focusRing: '#18295580',
    primary: {
      variant1: '#182955',
      variant2: '#F8F9FA',
      variant3: '#E9ECEF',
      variant4: '#E5E5E5',
      variant5: '#DEE2E6',
      variant6: '#CED4DA',
      variant7: '#ADB5BD',
      variant8: '#6C757D',
      variant9: '#495057',
      variant10: '#343A40',
      variant11: '#212529',
      variant12: '#157515',
      variant13: '#815f94',
      variant14: '#f9f1fa',
      variant15: '#014361',
      variant16: '#182955',
    },
    secondary: {
      variant1: '#5CD477',
      variant2: '#43A047',
      variant3: '#E8EEFF',
      variant4: '#3F6BDE',
      variant5: '#3459B8',
      variant6: '#7ce896',
      variant7: '#673AB7',
    },
    highlight: {
      variant1: '#EF6C00',
      variant2: '#D32F2F',
      variant3: '#B54FBA',
      variant4: '#E7DA0B',
      variant5: '#DE350B',
      variant6: '#E5F6FD',
    },
    rgba: {
      variant1: 'rgba(0, 0, 0, 0.45)',
      variant2: 'rgba(255, 255, 255, 0.2)',
      variant3: 'rgba(0, 0, 0, 0.7)',
      variant4: 'rgba(0, 0, 0, 0.87)',
      variant5: 'rgba(0, 0, 0, 0.23)',
    },
  },
};

const darkColors = {
  mode: 'dark' as const,
  colors: {
    white: '#FFFFFF',
    black: '#000000',
    transparent: 'transparent',
    /** Card/modal/list surface - matches WarrQueue background.paper */
    surface: '#121212',
    /** Page background - matches WarrQueue background.default */
    backgroundDefault: '#1E1E1E',
    /** Lower section background - matches WarrQueue work item view background.backdrop */
    backdrop: '#181818',
    /** Focus ring - matches WarrQueue card focus highlight (primary.highlight) */
    focusRing: '#4271EB80',
    primary: {
      variant1: '#85A7FF',
      variant2: '#1E1E1E',
      variant3: '#232323',
      variant4: '#232323',
      variant5: '#363636',
      variant6: '#757575',
      variant7: '#FFFFFF8F',
      variant8: '#FFFFFF8F',
      variant9: '#FFFFFFDE',
      variant10: '#FFFFFFDE',
      variant11: '#FFFFFF',
      variant12: '#66BB6A',
      variant13: '#CE93D8',
      variant14: '#1F2230',
      variant15: '#4D7FFF',
      variant16: '#85A7FF',
    },
    secondary: {
      variant1: '#66BB6A',
      variant2: '#43A047',
      variant3: '#1F2230',
      variant4: '#4D7FFF',
      variant5: '#85A7FF',
      variant6: '#81C784',
      variant7: '#CE93D8',
    },
    highlight: {
      variant1: '#FFA726',
      variant2: '#F44336',
      variant3: '#CE93D8',
      variant4: '#E7DA0B',
      variant5: '#DE350B',
      variant6: '#1F2A33',
    },
    rgba: {
      variant1: 'rgba(255, 255, 255, 0.45)',
      variant2: 'rgba(0, 0, 0, 0.2)',
      variant3: 'rgba(255, 255, 255, 0.7)',
      variant4: 'rgba(255, 255, 255, 0.87)',
      variant5: 'rgba(255, 255, 255, 0.23)',
    },
  },
};

export const mainThemeLight: DefaultTheme = {
  ...sharedTheme,
  ...lightColors,
};

export const mainThemeDark: DefaultTheme = {
  ...sharedTheme,
  ...darkColors,
};

/** @deprecated Use mainThemeLight or mainThemeDark via StyledThemeProvider */
const mainTheme: DefaultTheme = mainThemeLight;

export default mainTheme;
